import { Tag, Tooltip } from 'antd'
import ConnectivityIcon from 'components/ConnectivityIcon/ConnectivityIcon'
import connectivityIssuesTypes from 'constants/connectivityIssues'
import Heading from 'components/Heading'
import isEmpty from 'lodash/isEmpty'
import LastUpdateIcon from 'components/LastUpdateIcon'
import { LinkOutlined } from '@ant-design/icons'
import msToHoursAndMinutes from 'global/util/msToHoursAndMinutes'
import paths from 'constants/paths'
import portCallStatuses from 'constants/portCallStatuses'
import prettyPrintHoursAndMinutes from 'global/util/prettyPrintHoursAndMinutes'
import PropTypes from 'prop-types'
import React from 'react'
import Text from 'components/Text'
import { timeDateAndYear } from 'constants/time'
import Timestamp from 'components/Timestamp'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import ShipLoadingSkeleton from './ShipLoadingSkeleton'

const Ship = ({
  connectivityIssues,
  imoNumber,
  isCountryUnknown,
  isLoading,
  isShipOnline,
  msElapsedSinceLastUpdate,
  portCallClosed,
  portCallStatus,
  shipName,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { hours, minutes } = msToHoursAndMinutes(msElapsedSinceLastUpdate)
  const days = Math.floor(hours / 24)

  const timeElapsed = prettyPrintHoursAndMinutes({
    andLabel: t('global.time.and'),
    dayLabel: t('global.time.day'),
    days,
    hourLabel: t('global.time.hour'),
    hours,
    minuteLabel: t('global.time.minute'),
    minutes,
  })

  const getConnectivityStatusDetails = () => {
    switch (connectivityIssues) {
      case connectivityIssuesTypes.UNLIKELY:
        return {
          connectivityStatusColor: 'success',
          connectivityStatusLabel: t('connectivityIcon.unlikely'),
        }
      case connectivityIssuesTypes.LIKELY:
        return {
          connectivityStatusColor: 'warning',
          connectivityStatusLabel: t('connectivityIcon.likely'),
        }
      case connectivityIssuesTypes.VERY_LIKELY:
        return {
          connectivityStatusColor: 'error',
          connectivityStatusLabel: t('connectivityIcon.veryLikely'),
        }
      default:
        return {
          connectivityStatusColor: 'grey',
          connectivityStatusLabel: t('connectivityIcon.unknown'),
        }
    }
  }

  const { connectivityStatusColor, connectivityStatusLabel } =
    getConnectivityStatusDetails()

  return (
    <div className="general-card__value-container">
      {isLoading ? (
        <ShipLoadingSkeleton />
      ) : (
        <>
          <div className="ship-name__vessel-info">
            <Heading
              className="ship-name__heading"
              fontWeight="bold"
              hasNoMarginBottom
              level={4}
            >
              {shipName}
            </Heading>
            <Tooltip title={t('portCall.general.imoNumber')}>
              <Text isLabel>{`(${imoNumber})`}</Text>
            </Tooltip>
            <Tooltip title={t('portCall.general.goToShipPage')}>
              <LinkOutlined
                onClick={() => navigate(`${paths.ship.root}/${imoNumber}`)}
                style={{
                  cursor: 'pointer',
                  fontSize: '16px',
                  marginLeft: '8px',
                }}
              />
            </Tooltip>
          </div>

          <div className="ship-name__last-update-container">
            {portCallStatus === portCallStatuses.OPEN ? (
              <>
                <LastUpdateIcon
                  className="ship-name__last-update-icon"
                  isOnline={isShipOnline}
                />

                <Text>{`${t('portCall.general.lastUpdate')} ${
                  isEmpty(timeElapsed)
                    ? t('portCall.general.justNow')
                    : `${timeElapsed} ${t('global.time.ago')}`
                }`}</Text>
              </>
            ) : portCallStatus === portCallStatuses.CLOSED ? (
              <Timestamp
                date={portCallClosed}
                dateFormat={timeDateAndYear}
                isUTC={isCountryUnknown}
                title={t('portCall.general.portCallClosed')}
              />
            ) : (
              <Text>{t('portCall.general.interruptedPortCallClosed')}</Text>
            )}
          </div>

          <div className="ship-name__connectivity-container">
            <ConnectivityIcon
              className="ship-name__connectivity-icon"
              connectivityIssues={connectivityIssues}
            />
            <Text>
              {t('connectivityIcon.connectivity')}{' '}
              <Tag
                className="ship-name__connectivity-badge"
                color={connectivityStatusColor}
              >
                {connectivityStatusLabel}
              </Tag>
            </Text>
          </div>
        </>
      )}
    </div>
  )
}

Ship.defaultProps = {
  connectivityIssues: null,
  imoNumber: null,
  isShipOnline: null,
  msElapsedSinceLastUpdate: null,
  portCallClosed: null,
  portCallStatus: null,
  shipName: null,
}

Ship.propTypes = {
  connectivityIssues: PropTypes.oneOf([
    connectivityIssuesTypes.UNLIKELY,
    connectivityIssuesTypes.LIKELY,
    connectivityIssuesTypes.VERY_LIKELY,
  ]),
  imoNumber: PropTypes.number,
  isCountryUnknown: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isShipOnline: PropTypes.bool,
  msElapsedSinceLastUpdate: PropTypes.number,
  portCallClosed: PropTypes.string,
  portCallStatus: PropTypes.oneOf([
    portCallStatuses.OPEN,
    portCallStatuses.CLOSED,
    portCallStatuses.INTERRUPTED,
  ]),
  shipName: PropTypes.string,
}

export default Ship
